// Deterministic state machine for the Research Desk's open-idea book.
//
// Runs BEFORE the LLM. Decides:
//   - For each existing OPEN idea: today's status verb (reiterating /
//     upgrading / downgrading / closing-paid-off / closing-violated)
//   - Whether to introduce a NEW idea (and if so, ADD or SWAP, sourced from
//     which existing portfolio name)
//
// The LLM never decides whether to act — it only writes the prose.

import type { VelocityRow, FitRow } from './types';

export type IdeaType = 'add' | 'swap';

export type IdeaStatus = 'open' | 'closed-paid-off' | 'closed-violated';

export type NoteStatus =
  | 'initiating'
  | 'reiterating'
  | 'upgrading'
  | 'downgrading'
  | 'closing-paid-off'
  | 'closing-violated';

export type Conviction = 'low' | 'med' | 'high';

export interface NoteDrivers {
  composite: number;
  qDelta5d: number | null;
  vDelta5d: number | null;
  accel5: number | null;
  qShare5: number | null;
  alphaStreak: number;
}

export interface NoteFitSnapshot {
  betaSpy: number | null;
  maxCorr: { symbol: string; value: number } | null;
  sectorConcDelta: number | null;
}

export interface NoteEntry {
  date: string;
  status: NoteStatus;
  conviction: Conviction;
  thesisParagraph: string;
  whatChangedToday: string;
  // Sell-side report sections (added 2026-04-29, extended 2026-05-04 to match
  // the overnight-memo format Steve adopted as the canonical research shape).
  // All optional for backward compat with notes generated before the schema
  // extension — IdeaCard hides any section that's missing.
  industryContext?: string;
  companyOverview?: string;
  leadership?: string;            // management read, capital allocation, insiders
  competitivePosition?: string;   // moat sources, share dynamics, pricing power
  bookFit?: string;               // how this changes Steve's portfolio specifically
  theThesis?: string;
  bullCase?: string;              // explicit upside framing (replaces legacy whyOwnIt)
  bearCase?: string;              // explicit downside framing (replaces legacy downsideRisk)
  roomResponse?: string;          // addressing the four PM lenses (Asness/Simons/Cohen/Ackman)
  theAsk?: string;                // closing recommendation paragraph
  // Legacy fields kept for backward compat; new notes set bull/bear/ask instead.
  whyOwnIt?: string;
  expectedReturn?: string;
  downsideRisk?: string;
  drivers: NoteDrivers;
  fitSnapshot: NoteFitSnapshot;
  alertsConsidered: number;
}

export interface Idea {
  symbol: string;
  name: string;
  sector: string;
  type: IdeaType;
  swapSource: string | null;       // ticker to fund from, only when type='swap'
  initiationDate: string;
  lastNoteDate: string;
  status: IdeaStatus;
  closedDate: string | null;
  daysHeld: number | null;
  conviction: Conviction;
  originalThesis: string;          // frozen at initiation
  notes: NoteEntry[];              // appended on each refresh
}

export interface ResearchNotesConfig {
  maxOpenIdeas: number;            // 2 — Steve wants 1-2 active ideas
  paidOffStreakDays: number;       // 5 — close as paid_off after 5 alpha days
  violatedDropThreshold: number;   // -1.0 — composite drop in window triggers close
  violatedDropWindow: 'd5' | 'd20'; // window to evaluate the drop on
  upgradeDelta5dThreshold: number; // 0.25 — Δ5d above this and not high → upgrade
  downgradeDelta5dThreshold: number; // -0.25 — Δ5d below this and not low → downgrade
  addCorrThreshold: number;        // 0.5 — max-corr below this → ADD eligible
  addSectorDeltaThreshold: number; // 5 (pp) — sector delta below this → ADD eligible
  swapMinCompositeAdvantage: number; // 0.5 — candidate must beat source by this (was 0.2; raised 2026-04-29 to kill lateral swaps)
  violatedQuarantineDays: number;  // 30 — re-entry blocked for this many days
  // Quality bar — Steve runs a hedge fund, mediocre ideas waste his time.
  minCompositeForCandidate: number; // 3.0 — new ideas must be "strong" tier or above
  minCompositeForOpen: number;     // 2.0 — open ideas auto-close if composite drops below this
  minAccel5ForCandidate: number;   // 0 — candidate must be accelerating, not parked
}

export interface ResearchNotesFile {
  version: 1;
  config: ResearchNotesConfig;
  lastRunDate: string | null;
  open: Idea[];
  closed: Idea[];                  // newest first
}

export const DEFAULT_CONFIG: ResearchNotesConfig = {
  maxOpenIdeas: 2,
  paidOffStreakDays: 5,
  violatedDropThreshold: -1.0,
  violatedDropWindow: 'd20',
  upgradeDelta5dThreshold: 0.25,
  downgradeDelta5dThreshold: -0.25,
  addCorrThreshold: 0.5,
  addSectorDeltaThreshold: 5,
  swapMinCompositeAdvantage: 0.5,    // raised from 0.2 (2026-04-29)
  violatedQuarantineDays: 30,
  minCompositeForCandidate: 3.0,     // institutional bar — strong tier or none
  minCompositeForOpen: 2.0,          // auto-close below "investable"
  minAccel5ForCandidate: 0,          // must be accelerating
};

export function emptyState(): ResearchNotesFile {
  return {
    version: 1,
    config: DEFAULT_CONFIG,
    lastRunDate: null,
    open: [],
    closed: [],
  };
}

/**
 * Decide today's status verb for a single OPEN idea.
 * Closing rules win over direction rules.
 */
export function classifyOpenIdea(
  idea: Idea,
  v: VelocityRow,
  cfg: ResearchNotesConfig,
): NoteStatus {
  // Closing: paid off
  if (v.alphaStreak >= cfg.paidOffStreakDays) return 'closing-paid-off';

  // Closing: composite has fallen below the quality bar — Steve's directive
  // (2026-04-29) is to surface only strong ideas; open ideas that drop below
  // the bar get auto-retired.
  if (v.composite < cfg.minCompositeForOpen) return 'closing-violated';

  // Closing: thesis violated by composite drop in the chosen window
  const drop = v[cfg.violatedDropWindow]?.dcomp;
  if (drop != null && drop <= cfg.violatedDropThreshold) {
    return 'closing-violated';
  }

  // Direction
  const d5 = v.d5.dcomp;
  if (d5 == null) return 'reiterating';
  if (d5 >= cfg.upgradeDelta5dThreshold && idea.conviction !== 'high') return 'upgrading';
  if (d5 <= cfg.downgradeDelta5dThreshold && idea.conviction !== 'low') return 'downgrading';
  return 'reiterating';
}

/**
 * Tighten or loosen conviction in response to today's status verb. Pure mapping
 * function; the LLM is allowed to override.
 */
export function transitionConviction(prev: Conviction, status: NoteStatus): Conviction {
  if (status === 'upgrading') return prev === 'low' ? 'med' : 'high';
  if (status === 'downgrading') return prev === 'high' ? 'med' : 'low';
  return prev;
}

/**
 * For a CANDIDATE (not in portfolio, not currently open), decide whether to
 * recommend ADD or SWAP, and identify the swap source if applicable.
 *
 * Returns null if the candidate fails the gate (e.g. SWAP would not improve
 * the book).
 */
export function classifyCandidate(
  candidate: VelocityRow,
  fit: FitRow | undefined,
  velocityBySymbol: Map<string, VelocityRow>,
  cfg: ResearchNotesConfig,
): { type: IdeaType; swapSource: string | null } | null {
  const maxCorr = fit?.maxCorr?.value ?? 0;
  const sectorDelta = fit?.sectorConcDelta ?? 0;

  // ADD eligibility: low correlation to current book AND low sector delta
  if (maxCorr < cfg.addCorrThreshold && sectorDelta < cfg.addSectorDeltaThreshold) {
    return { type: 'add', swapSource: null };
  }

  // Otherwise SWAP. Source = the most-correlated portfolio name (the one being
  // duplicated). Reject the swap if the candidate doesn't materially beat it
  // on composite — no point swapping for a lateral move.
  const swapSource = fit?.maxCorr?.symbol ?? null;
  if (!swapSource) return null;
  const sourceRow = velocityBySymbol.get(swapSource);
  if (!sourceRow) return null;
  if (candidate.composite < sourceRow.composite + cfg.swapMinCompositeAdvantage) return null;

  return { type: 'swap', swapSource };
}

/**
 * Pick up to `slotsAvailable` candidates ranked by acceleration (or 5-day Δ
 * fallback), filtering out portfolio names, currently open names, and
 * recently-violated names. Each candidate is paired with an ADD/SWAP decision
 * via classifyCandidate; nulls are dropped (i.e. SWAPs that don't improve the
 * book are rejected).
 */
export interface ImpactGate {
  /** ΔSharpe required for the candidate to qualify (e.g. 0.02 = +2bp Sharpe lift) */
  minDeltaSharpe: number;
  /** ΔE[r] required (e.g. 0 = must not drag returns down) */
  minDeltaExpectedReturn: number;
}

export function pickCandidates(
  movers: VelocityRow[],
  velocityBySymbol: Map<string, VelocityRow>,
  fitBySymbol: Map<string, FitRow>,
  portfolio: Set<string>,
  openSymbols: Set<string>,
  recentlyViolated: Set<string>,
  cfg: ResearchNotesConfig,
  slotsAvailable: number,
  /** Optional: ΔSharpe / ΔE[r] map keyed by symbol. Candidates without an
   *  entry, or whose impact doesn't clear the gate, are dropped. */
  impactBySymbol?: Map<string, { deltaSharpe: number; deltaExpectedReturn: number }>,
  impactGate?: ImpactGate,
): Array<{ candidate: VelocityRow; type: IdeaType; swapSource: string | null }> {
  if (slotsAvailable <= 0) return [];

  const ranked = movers
    .filter(r =>
      !portfolio.has(r.symbol) &&
      !openSymbols.has(r.symbol) &&
      !recentlyViolated.has(r.symbol) &&
      r.q >= 0 &&
      r.composite >= cfg.minCompositeForCandidate &&
      (r.accel5 == null || r.accel5 >= cfg.minAccel5ForCandidate),
    )
    .slice()
    .sort((a, b) => {
      const ka = a.accel5 ?? a.d5.dcomp ?? -Infinity;
      const kb = b.accel5 ?? b.d5.dcomp ?? -Infinity;
      return kb - ka;
    });

  const out: Array<{ candidate: VelocityRow; type: IdeaType; swapSource: string | null }> = [];
  for (const cand of ranked) {
    if (out.length >= slotsAvailable) break;
    const decision = classifyCandidate(cand, fitBySymbol.get(cand.symbol), velocityBySymbol, cfg);
    if (!decision) continue;
    // Don't recommend swapping the same source twice in one run.
    if (decision.swapSource && out.some(o => o.swapSource === decision.swapSource)) continue;
    // Portfolio-impact gate — Steve's directive: every idea must improve the book.
    if (impactBySymbol && impactGate) {
      const impact = impactBySymbol.get(cand.symbol);
      if (!impact) continue;
      if (impact.deltaSharpe < impactGate.minDeltaSharpe) continue;
      if (impact.deltaExpectedReturn < impactGate.minDeltaExpectedReturn) continue;
    }
    out.push({ candidate: cand, type: decision.type, swapSource: decision.swapSource });
  }
  return out;
}

// Sell-side-style rating derived from status × conviction.
// The state machine determines the verb; this maps it to the action a PM
// reading the report can act on directly.
export type Rating = 'BUY' | 'HOLD' | 'SELL';

export function deriveRating(idea: Idea): Rating {
  const last = idea.notes[idea.notes.length - 1];
  if (!last) return 'HOLD';
  switch (last.status) {
    case 'closing-violated':
      return 'SELL';
    case 'closing-paid-off':
      // Took the win — stop adding. PM may continue to hold the position
      // outside the desk's coverage; the desk itself is done.
      return 'HOLD';
    case 'initiating':
    case 'upgrading':
      return 'BUY';
    case 'downgrading':
      return idea.conviction === 'low' ? 'HOLD' : 'BUY';
    case 'reiterating':
      return idea.conviction === 'low' ? 'HOLD' : 'BUY';
  }
}

/**
 * Find symbols that were closed-violated within the last `quarantineDays`.
 * `today` is YYYY-MM-DD.
 */
export function recentlyViolated(
  closed: Idea[],
  today: string,
  quarantineDays: number,
): Set<string> {
  const cutoff = new Date(today);
  cutoff.setDate(cutoff.getDate() - quarantineDays);
  const cutoffStr = cutoff.toISOString().slice(0, 10);
  const out = new Set<string>();
  for (const idea of closed) {
    if (idea.status !== 'closed-violated') continue;
    if (!idea.closedDate) continue;
    if (idea.closedDate >= cutoffStr) out.add(idea.symbol);
  }
  return out;
}
